import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NotificationSettingsModal from 'src/app/Dashboard/NotificationSettingsModal';
import NotificationCmp, { FilterType } from 'src/components/NotificationCmp';
import { PATH } from 'src/constants/path';
import { setCurrentUser } from 'src/redux/auth.slice';
import { type AppDispatch, type RootState } from 'src/redux/store';
import { INotificationTypes, saveNotificationSettings } from 'src/services/CustomerService';
import { clearAllNotifications, getNotifications, markNotificationsAsRead, transformNotification, NotificationItem } from 'src/services/NotificationService';

const MODULE_NAME_MAP: Record<Exclude<FilterType, 'all'>, string> = {
    quote: 'QUOTE',
    order: 'ORDER',
};

const NOTIFICATION_LIMIT = 6;

interface IProps {
    handleClose: () => void;
    initialNotifications?: any[];
    initialLoading?: boolean;
    initialTotals?: number;
    initialHasNextPage?: boolean;
    onNotificationUpdate?: (notifications: any[], totals: number, hasNextPage: boolean) => void;
    // Header badge count (unread only). The list totals above can't drive the badge any more:
    // read rows are kept, so totalItems counts history while the badge must count unread.
    unreadTotal?: number;
    onUnreadTotalChange?: (unreadTotal: number) => void;
}

const Notification = ({
    handleClose,
    initialNotifications = [],
    initialLoading = false,
    initialTotals = 0,
    initialHasNextPage = false,
    onNotificationUpdate,
    unreadTotal = 0,
    onUnreadTotalChange,
}: IProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { appearance } = useSelector((state: RootState) => state.basic);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const [notFoundTitle] = useState<string>('No notifications yet');
    const [notFoundDesc] = useState<string>('Updates about your quotes and orders will appear here.');
    const [notifications, setNotifications] = useState<any[]>(initialNotifications);
    const [isLoading, setIsLoading] = useState<boolean>(initialLoading);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [totals, setTotals] = useState<number>(initialTotals);
    const [hasNextPage, setHasNextPage] = useState<boolean>(initialHasNextPage);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [activeFilter, setActiveFilter] = useState<FilterType>('all');
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    // Prevents the prop-sync effect from overriding isLoading while a filter-change fetch is in flight
    const filterChangingRef = useRef(false);

    // Sync with initial props when they change — skip isLoading override during filter changes
    useEffect(() => {
        setNotifications(initialNotifications);
        setTotals(initialTotals);
        setHasNextPage(initialHasNextPage);

        if (!filterChangingRef.current) {
            setIsLoading(initialLoading);
        }
    }, [initialNotifications, initialTotals, initialHasNextPage, initialLoading]);

    // Update parent when notifications change
    useEffect(() => {
        if (onNotificationUpdate && notifications !== initialNotifications) {
            onNotificationUpdate(notifications, totals, hasNextPage);
        }
    }, [notifications, totals, hasNextPage]);

    const fetchMoreNotifications = useCallback(
        async (page: number, filter: FilterType = 'all', isReset = false) => {
            try {
                if (isReset) {
                    setIsLoading(true);
                } else {
                    setIsLoadingMore(true);
                }

                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }

                abortControllerRef.current = new AbortController();

                const params: { page: number; limit: number; moduleName?: string } = {
                    page,
                    limit: NOTIFICATION_LIMIT,
                };

                if (filter !== 'all') {
                    params.moduleName = MODULE_NAME_MAP[filter];
                }

                const response = await getNotifications(params, abortControllerRef.current.signal);

                if (response?.data?.notifications) {
                    const transformedNotifications = response.data.notifications.map((notification: NotificationItem) => transformNotification(notification));

                    setNotifications((prev) => {
                        const existingIds = new Set(prev.map((n) => n.id));
                        const newNotifications = transformedNotifications.filter((n: any) => !existingIds.has(n.id));

                        return [...prev, ...newNotifications];
                    });
                    setTotals(response.data.pagination?.totalItems || 0);
                    setHasNextPage(response.data.pagination?.hasNextPage || false);
                    setCurrentPage(page);

                    if (typeof response.data.unreadTotal === 'number' && onUnreadTotalChange) {
                        onUnreadTotalChange(response.data.unreadTotal);
                    }
                }
            } catch (error: any) {
                if (error.code !== 'ERR_CANCELED') {
                    console.error('Error fetching notifications:', error);
                }
            } finally {
                if (isReset) {
                    filterChangingRef.current = false;
                    setIsLoading(false);
                } else {
                    setIsLoadingMore(false);
                }
            }
        },
        [onUnreadTotalChange]
    );

    useEffect(
        () => () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        },
        []
    );

    const handleSaveNotificationSettings = async (notificationTypes: INotificationTypes) => {
        const response = await saveNotificationSettings({ notificationTypes });

        dispatch(
            setCurrentUser({
                ...currentUser,
                notificationSettings: response?.data?.notificationSettings ?? { notificationTypes },
            })
        );
    };

    const handleFilterChange = useCallback(
        (filter: FilterType) => {
            filterChangingRef.current = true;
            setIsLoading(true);
            setActiveFilter(filter);
            setNotifications([]);
            setCurrentPage(1);
            setHasNextPage(false);
            fetchMoreNotifications(1, filter, true);
        },
        [fetchMoreNotifications]
    );

    const handleLoadMore = useCallback(() => {
        if (!isLoadingMore && hasNextPage) {
            fetchMoreNotifications(currentPage + 1, activeFilter);
        }
    }, [isLoadingMore, hasNextPage, currentPage, activeFilter, fetchMoreNotifications]);

    const handleNotificationStatus = (isSingle: boolean, notificationId: number | number[] | null = null) => {
        // Normalize single-id and array-id inputs into a single ids[] array. `isSingle` is preserved
        // for backwards compatibility and only signals the call shape, not the behavior.
        const ids: number[] = isSingle && typeof notificationId === 'number' ? [notificationId] : Array.isArray(notificationId) ? notificationId : [];

        if (ids.length === 0) return;

        // Reads are non-destructive: the row stays in the list as read history and only loses its
        // unread highlight. Totals and pagination are untouched — only the unread badge moves.
        const idSet = new Set(ids);
        const flippedCount = notifications.filter((n) => idSet.has(n.id) && n.isNew).length;
        const updatedNotifications = notifications.map((n) => (idSet.has(n.id) ? { ...n, isNew: false, isRead: true } : n));

        setNotifications(updatedNotifications);

        // Directly sync parent immediately — the useEffect that does this won't fire if this
        // component unmounts (e.g. after navigation) before the state update renders
        if (onNotificationUpdate) {
            onNotificationUpdate(updatedNotifications, totals, hasNextPage);
        }

        if (flippedCount > 0 && onUnreadTotalChange) {
            onUnreadTotalChange(Math.max(0, unreadTotal - flippedCount));
        }

        // Fire API in background — component may already be unmounted by the time this resolves
        markNotificationsAsRead(ids).catch((error) => {
            console.error('Error marking notifications as read:', error);
        });
    };

    const handleClearAll = async () => {
        // Mark-all-as-read: every row stays and un-tints; only the badge drops to zero.
        const updatedNotifications = notifications.map((n) => (n.isNew ? { ...n, isNew: false, isRead: true } : n));

        setNotifications(updatedNotifications);

        if (onNotificationUpdate) {
            onNotificationUpdate(updatedNotifications, totals, hasNextPage);
        }

        if (onUnreadTotalChange) {
            onUnreadTotalChange(0);
        }

        try {
            await clearAllNotifications();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleNotificationClick = (notification: any) => {
        // Mark notification as read (this will also remove it from the list)
        handleNotificationStatus(true, notification.id);

        const isMessageAction = notification.metadata?.action?.toLowerCase() === 'message';

        // Navigate based on module type
        if (notification.moduleName === 'QUOTE' && notification.referenceId) {
            handleClose();
            navigate(`${PATH.MY_QUOTE}/${notification.referenceId}`, {
                state: isMessageAction ? { openMessagesTab: true } : undefined,
            });
        } else if (notification.moduleName === 'ORDER' && notification.referenceId) {
            handleClose();
            navigate(`${PATH.ORDER_VIEW}/${notification.referenceId}`, {
                state: isMessageAction ? { openMessagesTab: true } : undefined,
            });
        }
    };

    return (
        <>
            <NotificationCmp
                handleNotificationClick={handleNotificationClick}
                handleNotificationStatus={handleNotificationStatus}
                handleClearAll={handleClearAll}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                numberOfSkeletonRows={NOTIFICATION_LIMIT}
                notifications={notifications}
                notFoundTitle={notFoundTitle}
                notFoundDesc={notFoundDesc}
                handleClose={handleClose}
                hasNextPage={hasNextPage}
                onLoadMore={handleLoadMore}
                timezone={appearance?.timezone}
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
                onSettingsClick={() => setIsSettingsOpen(true)}
                salesRepName={currentUser?.salesRep?.salesrepname || ''}
                orgName={appearance?.web_title || ''}
            />

            {isSettingsOpen && (
                <NotificationSettingsModal
                    handleClose={() => setIsSettingsOpen(false)}
                    existingData={currentUser?.notificationSettings?.notificationTypes ?? null}
                    onSave={handleSaveNotificationSettings}
                />
            )}
        </>
    );
};

export default Notification;
